import { useAxios } from 'hooks/useAxios';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Locale } from 'utils/localization';

type DownloadBlueprintPdfParams = {
  blueprintId: number;
  fileName?: string;
  screenshot?: Blob | null;
};

export const useDownloadBlueprintPdf = (locale: Locale = 'nl-NL') => {
  const { axiosRequest } = useAxios();
  const { i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const downloadBlueprintPdf = async ({ blueprintId, fileName, screenshot }: DownloadBlueprintPdfParams) => {
    setIsLoading(true);
    setIsError(false);
    setError(null);

    try {
      const formData = new FormData();
      if (screenshot) {
        formData.append('power_profile_image', screenshot, 'power-profile.png');
      }

      const response = await axiosRequest({
        method: 'POST',
        path: `/blueprints/${blueprintId}/pdf?lang=${i18n.language || 'en'}&locale=${locale}`,
        body: formData,
        headers: {
          Accept: 'application/pdf',
        },
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'application/pdf/' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || `solution-${blueprintId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true };
    } catch (err) {
      const error = err instanceof Error ? err : new Error('Failed to download PDF');
      setIsError(true);
      setError(error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    downloadBlueprintPdf,
    isLoading,
    isError,
    error,
  };
};
